import {
  Game,
  LineupEntry,
  LineupPositions,
  PlayerPublic,
  SideType,
  TeamDB,
} from "@aura-frontend/omnibus-az-client";

export const getHomeAwayLineups = (
  game: Game | undefined | null,
): { home: LineupEntry[]; away: LineupEntry[] } => {
  const lineups = game?.lineups ?? {};
  const result = {
    home: lineups.homeLineup ?? [],
    away: lineups.awayLineup ?? [],
  };
  return result;
};

/**
 * Map of handedness to side type
 */
export const sideTypeMap: Record<THandedness, SideType> = {
  R: "right",
  L: "left",
  S: "switch",
};

/**
 * Map of side type to handedness
 */
export const sideTypeToHandednessMap: Record<SideType, THandedness> = {
  right: "R",
  left: "L",
  switch: "S",
};

const toEntryJerseyNumber = (jerseyNumber: IPlayer["jerseyNumber"]) => {
  const value = parseInt(String(jerseyNumber ?? ""), 10);
  return Number.isNaN(value) ? undefined : value;
};

/**
 * Map an IPlayer to a LineupEntry. Omnibus substitutions only accept an
 * outgoing player that matches its lineup entry exactly, jersey number
 * included, so a player read from a lineup must map back to the same entry.
 * @param player - The player to map
 * @param position - The position of the player in the lineup
 * @returns The lineup entry
 */
export const mapIPlayerToLineupEntry = (
  player: IPlayer,
  position: number | LineupPositions,
): LineupEntry => {
  const jerseyNumber = toEntryJerseyNumber(player.jerseyNumber);
  const entry: LineupEntry = {
    externalId: player.uuid,
    hitSide: sideTypeMap[player.batHand],
    pitchSide: sideTypeMap[player.throwHand],
    playerName: `${player.lastName}, ${player.firstName}`.trim(),
    ...(jerseyNumber !== undefined && { jerseyNumber }),
    position,
  };
  return entry;
};

/**
 * Parse a player display name into first/last components. Accepts either our
 * internal "Last, First" format or an external "First Last" format.
 */
const parsePlayerName = (
  name: string | null | undefined,
): { firstName: string; lastName: string } => {
  if (!name) return { firstName: "", lastName: "" };
  if (name.includes(",")) {
    const [last, first] = name.split(",");
    return { firstName: first?.trim() ?? "", lastName: last?.trim() ?? "" };
  }
  const tokens = name.trim().split(/\s+/);
  if (tokens.length === 1) return { firstName: "", lastName: tokens[0] };
  const [first, ...rest] = tokens;
  return { firstName: first, lastName: rest.join(" ") };
};

/**
 * Map an IPlayer to a LineupPair
 * @param player - The player to map
 * @param position - The position of the player in the lineup
 * @returns The lineup pair
 */
export const mapLineupEntryToIPLayer = (
  player: LineupEntry,
): IPlayer & { position: number | LineupPositions } => {
  const playerName = player.playerName ?? "";
  const externalId = player.externalId ?? "";
  const hitSide = player.hitSide as SideType | undefined;
  const pitchSide = player.pitchSide as SideType | undefined;

  const { firstName, lastName } = parsePlayerName(playerName);

  const result: IPlayer & { position: number | LineupPositions } = {
    uuid: externalId,
    firstName,
    lastName,
    birthdate: "",
    // Kept so a substitution sends the player back as the lineup has them
    jerseyNumber:
      player.jerseyNumber != null ? String(player.jerseyNumber) : "",
    batHand: hitSide ? sideTypeToHandednessMap[hitSide] : undefined!,
    throwHand: pitchSide ? sideTypeToHandednessMap[pitchSide] : undefined!,
    position: player.position,
  };

  return result;
};

/**
 * Map a public player to an IPlayer
 * @param player - The public player to map
 * @returns The IPlayer
 */
export const mapPublicPlayerToIPlayer = (player: PlayerPublic): IPlayer => {
  const { firstName, lastName } = parsePlayerName(player.known_names?.[0]);
  // Served but missing from the generated type. useGetActiveGame sets it to
  // the roster's number (withLineupJerseyNumbers).
  const jerseyNumber = (player as PlayerPublic & { jersey_number?: number })
    .jersey_number;

  return {
    // TODO: ID is not returned from API
    uuid: player.external_ids?.[0] ?? "",
    firstName,
    lastName,
    birthdate: "",
    jerseyNumber: jerseyNumber != null ? String(jerseyNumber) : "",
    batHand: sideTypeToHandednessMap[player.hit_side as SideType],
    throwHand: sideTypeToHandednessMap[player.pitch_side as SideType],
  };
};

/**
 * Map a TeamDB to an ITeam
 * @param team - The TeamDB to map
 * @returns The ITeam
 */
export const mapTeamDBToITeam = (team: TeamDB): ITeam => {
  return {
    uuid: team.external_ids?.[0] ?? "",
    name: team.known_names?.[0] ?? "",
    sport: team.sport ?? "",
  };
};
